import {
  useCategoriesByCostCenter,
  useCategoriesByDonationCostCenter,
  useCategoriesList,
} from '@/api/category/category.api';
import {
  useCostCenterByCategory,
  useCostCenterByDonation,
  useGetGlobalCostCenter,
} from '@/api/costCenter/costCenter.api';
import { useDonationsByCostCenter } from '@/api/donation/donation.api';
import { useLedgerAccountChildrens } from '@/api/ledgerAccount/ledgerAccount.api';
import { CURRENCY_OPTIONS, LEGACY_GLOBAL_CATEGORY_NAME } from '@/common/constants';
import { CurrencyOption } from '@/common/types';
import { SelectFilter } from '@/components/shared/SelectFilter';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { fixAmount, getRequiredLabel } from '@/lib/utils';
import { Trash } from 'lucide-react';
import { FC, useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { AccountingEntryItemSchema } from './AccountingEntryItemSchema';

interface AccountingEntryItemProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any, unknown>;
  index: number;
  usePrepopulatedAccount?: boolean;
  onDelete: (index: number) => void;
  showAllocationFields?: boolean;
  fixedDonationId?: string;
  allowDonationSelection?: boolean;
  showGlobalCheckbox?: boolean;
}

const getDebitInArs = (
  amount: number,
  exchangeRateOfficial: number,
  currency: CurrencyOption['value'],
) => {
  if (amount >= 0) {
    if (currency === 'ARS') {
      return amount;
    }

    return fixAmount(amount * exchangeRateOfficial, 2);
  }

  return undefined;
};

const getCreditInArs = (
  amount: number,
  exchangeRateOfficial: number,
  currency: CurrencyOption['value'],
) => {
  if (amount < 0) {
    if (currency === 'ARS') {
      return -amount;
    }

    return fixAmount(-amount * exchangeRateOfficial, 2);
  }

  return 0;
};

const AccountingEntryItem: FC<AccountingEntryItemProps> = ({
  form,
  index,
  usePrepopulatedAccount,
  onDelete,
  showAllocationFields = false,
  fixedDonationId = '',
  allowDonationSelection = false,
  showGlobalCheckbox = false,
}) => {
  const { data: ledgerAccounts, isLoading: isLoadingLedgerAccounts } = useLedgerAccountChildrens();
  const { data: categories, isLoading: isLoadingCategories } = useCategoriesList();
  const { data: globalCostCenter } = useGetGlobalCostCenter();
  const accountingEntry = useWatch({
    control: form.control,
    name: `accountingEntries.${index}`,
  });
  const isGlobalEntry = Boolean(accountingEntry?.global);
  const selectedCategoryId = accountingEntry?.category?.id || '';
  const selectedDonationId = fixedDonationId || accountingEntry?.donation?.id || '';
  const selectedCostCenterId = accountingEntry?.costCenter?.id || '';
  const isFixedDonationAllocation = Boolean(showAllocationFields && fixedDonationId);
  const { data: costCentersByDonation, isLoading: isLoadingCostCentersByDonation } =
    useCostCenterByDonation(isFixedDonationAllocation ? selectedDonationId : '');
  const {
    data: categoriesByDonationCostCenter,
    isLoading: isLoadingCategoriesByDonationCostCenter,
  } = useCategoriesByDonationCostCenter(
    isFixedDonationAllocation ? selectedDonationId : '',
    isFixedDonationAllocation ? selectedCostCenterId : '',
  );
  const { data: costCentersByCategory, isLoading: isLoadingCostCentersByCategory } =
    useCostCenterByCategory(showGlobalCheckbox && !isGlobalEntry ? selectedCategoryId : '');
  const { data: donationsByCostCenter, isLoading: isLoadingDonationsByCostCenter } =
    useDonationsByCostCenter(
      showGlobalCheckbox && !isGlobalEntry ? selectedCostCenterId : '',
      showGlobalCheckbox && !isGlobalEntry ? selectedCategoryId : '',
    );
  const { data: categoriesByCostCenter, isLoading: isLoadingCategoriesByCostCenter } =
    useCategoriesByCostCenter(
      showGlobalCheckbox && isGlobalEntry ? globalCostCenter?.id || selectedCostCenterId : '',
    );
  const amount = accountingEntry?.amount ?? 0;
  const exchangeRateOfficial = accountingEntry?.exchangeRateOfficial ?? 1;
  const currency = (accountingEntry?.currency || 'ARS') as CurrencyOption['value'];

  const availableCostCenters = showGlobalCheckbox
    ? isGlobalEntry
      ? globalCostCenter
        ? [
            {
              id: globalCostCenter.id,
              name: globalCostCenter.name || globalCostCenter.code,
              global: true,
            },
          ]
        : []
      : (costCentersByCategory || []).filter((costCenter) => !costCenter.global)
    : costCentersByDonation || [];
  const filterForwardCategories = <T extends { name?: string }>(options: Array<T>): Array<T> =>
    options.filter((category) => category.name !== LEGACY_GLOBAL_CATEGORY_NAME);
  const availableCategories = showGlobalCheckbox
    ? isGlobalEntry
      ? filterForwardCategories(categoriesByCostCenter || [])
      : filterForwardCategories(categories || [])
    : isFixedDonationAllocation
      ? categoriesByDonationCostCenter || []
      : [];
  const availableDonations =
    showGlobalCheckbox && !isGlobalEntry ? donationsByCostCenter || [] : [];

  useEffect(() => {
    if (!showGlobalCheckbox || !isGlobalEntry || !selectedCategoryId || !globalCostCenter) {
      return;
    }

    if (selectedCostCenterId === globalCostCenter.id) {
      return;
    }

    form.setValue(
      `accountingEntries.${index}.costCenter`,
      {
        id: globalCostCenter.id,
        name: globalCostCenter.name || globalCostCenter.code,
        global: true,
      },
      { shouldValidate: true },
    );
  }, [
    form,
    globalCostCenter,
    index,
    isGlobalEntry,
    selectedCategoryId,
    selectedCostCenterId,
    showGlobalCheckbox,
  ]);

  const handleInvoiceLikeCategoryChange = (value: { id: string; name: string }) => {
    form.setValue(
      `accountingEntries.${index}.category`,
      {
        id: value.id,
        name: value.name,
      },
      { shouldValidate: true },
    );

    if (isGlobalEntry) {
      form.setValue(`accountingEntries.${index}.donation`, null, { shouldValidate: true });
      if (globalCostCenter) {
        form.setValue(
          `accountingEntries.${index}.costCenter`,
          {
            id: globalCostCenter.id,
            name: globalCostCenter.name || globalCostCenter.code,
            global: true,
          },
          { shouldValidate: true },
        );
      }
      return;
    }

    form.setValue(`accountingEntries.${index}.costCenter`, null, { shouldValidate: true });
    form.setValue(`accountingEntries.${index}.donation`, null, { shouldValidate: true });
  };

  const handleInvoiceLikeCostCenterChange = (value: {
    id: string;
    name: string;
    global?: boolean;
  }) => {
    form.setValue(
      `accountingEntries.${index}.costCenter`,
      {
        id: value.id,
        name: value.name,
        global: value.global,
      },
      { shouldValidate: true },
    );
    form.setValue(`accountingEntries.${index}.donation`, null, { shouldValidate: true });
  };

  const handleInvoiceLikeDonationChange = (value: {
    id: string;
    name: string;
    money?: { currency: string; amount?: number | null; exchangeRate?: number | null };
  }) => {
    form.setValue(
      `accountingEntries.${index}.donation`,
      {
        id: value.id,
        name: value.name,
        money: value.money,
      },
      { shouldValidate: true },
    );
  };

  const handleGlobalChange = (checked: boolean) => {
    form.setValue(`accountingEntries.${index}.global`, checked);
    form.setValue(`accountingEntries.${index}.category`, null);
    form.setValue(`accountingEntries.${index}.donation`, null);
    form.setValue(`accountingEntries.${index}.costCenter`, null);
  };

  const handleFixedDonationCostCenterChange = (value: {
    id: string;
    name: string;
    global?: boolean;
  }) => {
    form.setValue(
      `accountingEntries.${index}.costCenter`,
      {
        id: value.id,
        name: value.name,
        global: value.global,
      },
      { shouldValidate: true },
    );
    form.setValue(`accountingEntries.${index}.category`, null, { shouldValidate: true });
  };

  const availableLegacyCategories = categoriesByDonationCostCenter || [];

  const handleChangeCurrency = (value: string) => {
    form.setValue(`accountingEntries.${index}.currency`, value);
    if (value === 'USD' || value === 'ARS') {
      form.setValue(`accountingEntries.${index}.exchangeRateOfficial`, 1);
      form.setValue(`accountingEntries.${index}.exchangeRateAlt`, 1);
    }
  };

  return (
    <Card className="relative p-6 mt-4">
      <Button
        type="button"
        onClick={() => onDelete(index)}
        variant="outlineDestructive"
        className="absolute right-4"
      >
        <Trash className="w-4 h-4" />
      </Button>
      <div className="flex flex-col gap-6">
        <SelectFilter
          form={form}
          formLabel={getRequiredLabel(
            AccountingEntryItemSchema,
            'ledgerAccount',
            'Cuenta Contable',
          )}
          formName={`accountingEntries.${index}.ledgerAccount`}
          options={ledgerAccounts || []}
          formPlaceholder="Selecciona una cuenta"
          formFilterPlaceholder="Buscar cuenta"
          isLoading={isLoadingLedgerAccounts}
          classnames={{ button: 'w-full', item: 'w-1/2' }}
          disabled={usePrepopulatedAccount}
        />
        {showAllocationFields ? (
          <div className="flex flex-col gap-4">
            {showGlobalCheckbox ? (
              <FormField
                control={form.control}
                name={`accountingEntries.${index}.global`}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start">
                    <FormControl>
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) => handleGlobalChange(Boolean(checked))}
                        disabled={!globalCostCenter}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">Gasto Global</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            ) : null}
            <div className={`grid gap-4 ${allowDonationSelection ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {showGlobalCheckbox ? (
                <>
                  <SelectFilter
                    form={form}
                    formLabel="Categoría (Opcional)"
                    formName={`accountingEntries.${index}.category`}
                    options={availableCategories}
                    formPlaceholder="Selecciona una categoría"
                    formFilterPlaceholder="Buscar categoría..."
                    isLoading={
                      isGlobalEntry ? isLoadingCategoriesByCostCenter : isLoadingCategories
                    }
                    onChange={handleInvoiceLikeCategoryChange}
                    classnames={{ button: 'w-full', item: 'w-full' }}
                    disabled={isGlobalEntry ? isLoadingCategoriesByCostCenter : isLoadingCategories}
                  />
                  <SelectFilter
                    form={form}
                    formLabel="Centro de Costos (Opcional)"
                    formName={`accountingEntries.${index}.costCenter`}
                    options={availableCostCenters}
                    formPlaceholder="Selecciona un centro de costos"
                    formFilterPlaceholder="Buscar centro de costos..."
                    isLoading={
                      !isGlobalEntry && selectedCategoryId ? isLoadingCostCentersByCategory : false
                    }
                    onChange={handleInvoiceLikeCostCenterChange}
                    classnames={{ button: 'w-full', item: 'w-full' }}
                    disabled={
                      isGlobalEntry || !selectedCategoryId || isLoadingCostCentersByCategory
                    }
                  />
                  <SelectFilter
                    form={form}
                    formLabel="Donación (Opcional)"
                    formName={`accountingEntries.${index}.donation`}
                    options={availableDonations}
                    formPlaceholder={isGlobalEntry ? '-' : 'Selecciona una donación'}
                    formFilterPlaceholder="Buscar donación..."
                    isLoading={
                      !isGlobalEntry && selectedCategoryId && selectedCostCenterId
                        ? isLoadingDonationsByCostCenter
                        : false
                    }
                    onChange={handleInvoiceLikeDonationChange}
                    classnames={{ button: 'w-full', item: 'w-full' }}
                    disabled={
                      isGlobalEntry ||
                      !selectedCategoryId ||
                      !selectedCostCenterId ||
                      isLoadingDonationsByCostCenter
                    }
                  />
                </>
              ) : (
                <>
                  {allowDonationSelection ? (
                    <SelectFilter
                      form={form}
                      formLabel="Donación (Opcional)"
                      formName={`accountingEntries.${index}.donation`}
                      options={[]}
                      selectedOption={fixedDonationId ? accountingEntry?.donation?.name : undefined}
                      formPlaceholder="Selecciona una donación"
                      formFilterPlaceholder="Buscar donación..."
                      isLoading={false}
                      classnames={{ button: 'w-full', item: 'w-full' }}
                      disabled
                    />
                  ) : null}
                  <SelectFilter
                    form={form}
                    formLabel="Centro de Costos (Opcional)"
                    formName={`accountingEntries.${index}.costCenter`}
                    options={availableCostCenters}
                    formPlaceholder="Selecciona un centro de costos"
                    formFilterPlaceholder="Buscar centro de costos..."
                    isLoading={selectedDonationId ? isLoadingCostCentersByDonation : false}
                    onChange={handleFixedDonationCostCenterChange}
                    classnames={{ button: 'w-full', item: 'w-full' }}
                    disabled={!selectedDonationId || isLoadingCostCentersByDonation}
                  />
                  <SelectFilter
                    form={form}
                    formLabel="Categoría (Opcional)"
                    formName={`accountingEntries.${index}.category`}
                    options={availableLegacyCategories}
                    formPlaceholder="Selecciona una categoría"
                    formFilterPlaceholder="Buscar categoría..."
                    isLoading={isLoadingCategoriesByDonationCostCenter}
                    classnames={{ button: 'w-full', item: 'w-full' }}
                    disabled={
                      !selectedCostCenterId ||
                      !selectedDonationId ||
                      isLoadingCategoriesByDonationCostCenter
                    }
                  />
                </>
              )}
            </div>
          </div>
        ) : null}
        <div className="flex flex-row gap-4">
          <FormField
            control={form.control}
            name={`accountingEntries.${index}.currency`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {getRequiredLabel(AccountingEntryItemSchema, 'currency', 'Moneda')}
                </FormLabel>
                <Select onValueChange={(value) => handleChangeCurrency(value)} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione la Moneda" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`accountingEntries.${index}.amount`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {getRequiredLabel(AccountingEntryItemSchema, 'amount', 'Monto')}
                </FormLabel>
                <FormControl>
                  <CurrencyInput
                    {...field}
                    type="number"
                    currency={currency}
                    placeholder="Ingresar monto"
                    allowNegative={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {currency !== 'ARS' && (
            <FormField
              control={form.control}
              name={`accountingEntries.${index}.exchangeRateOfficial`}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {getRequiredLabel(
                      AccountingEntryItemSchema,
                      'exchangeRateOfficial',
                      'Tipo de Cambio Oficial',
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="Ingresar Conversión" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name={`accountingEntries.${index}.exchangeRateAlt`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {getRequiredLabel(
                    AccountingEntryItemSchema,
                    'exchangeRateAlt',
                    'Tipo de Cambio Alt.',
                  )}
                </FormLabel>
                <FormControl>
                  <Input {...field} type="number" placeholder="Ingresar Conversión" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-row gap-4">
          <FormItem className="w-1/2">
            <FormLabel>Debe en ARS</FormLabel>
            <FormControl>
              <CurrencyInput
                type="number"
                currency="ARS"
                placeholder="Ingresar monto"
                disabled
                value={getDebitInArs(amount, exchangeRateOfficial, currency)}
                onChange={() => {}}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
          <FormItem className="w-1/2">
            <FormLabel>Haber en ARS</FormLabel>
            <FormControl>
              <CurrencyInput
                type="number"
                currency="ARS"
                placeholder="Ingresar monto"
                disabled
                value={getCreditInArs(amount, exchangeRateOfficial, currency)}
                onChange={() => {}}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </div>
        <div className="flex flex-row gap-4">
          <FormField
            control={form.control}
            name={`accountingEntries.${index}.details`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {getRequiredLabel(AccountingEntryItemSchema, 'details', 'Detalle')}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Escriba aquí el detalle..."
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </Card>
  );
};

export default AccountingEntryItem;
